// Team Member Management API (Laravel REST)
// Replaces legacy Arc User JSON-RPC endpoints

const apiTeamMembersUrl = `${arc.path.base}api/team-members`;
const apiCrmCredentialsUrl = `${arc.path.base}api/crm/team-member-credentials`;

function readCookie(name) {
    const match = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Shared fetch wrapper with proper error handling.
 * Throws Error with server message on failure.
 */
async function fetchJSON(url, options = {}) {
    // These endpoints run under the session the Arc gateway login creates, so
    // every write has to carry the CSRF token Laravel put in the cookie.
    const xsrfToken = readCookie('XSRF-TOKEN');

    const res = await fetch(url, {
        ...options,
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(xsrfToken ? { 'X-XSRF-TOKEN': xsrfToken } : {}),
            ...options.headers,
        },
    });

    let data;
    try {
        data = await res.json();
    } catch (e) {
        throw new Error(`Server returned invalid response (${res.status} ${res.statusText})`);
    }

    // Server error (non-2xx)
    if (!res.ok) {
        // Laravel validation errors (the permission refusal sends an empty list)
        if (data.errors && Object.keys(data.errors).length) {
            const firstError = Object.values(data.errors)[0];
            throw new Error(Array.isArray(firstError) ? firstError[0] : firstError);
        }
        // CRM endpoints report the reason as `error`; the REST ones use `message`.
        throw new Error(data.error || data.message || `Request failed (${res.status})`);
    }

    // API-level error (success: false)
    if (data && data.success === false) {
        throw new Error(data.message || 'API error');
    }

    return data;
}

/**
 * Get a single team member by ID.
 * 
 * @param {number} teamMemberId - ARCIMEDES_ROW_ID
 * @returns {Promise<Object>} Team member data
 */
export async function getTeamMember(teamMemberId) {
    return fetchJSON(`${apiTeamMembersUrl}/${teamMemberId}`);
}

/**
 * Search team members by email (or other fields).
 * 
 * @param {string} searchTerm - Email, name, or username
 * @returns {Promise<Object>} { success, data: [...], count }
 */
export async function searchTeamMembers(searchTerm) {
    const url = `${apiTeamMembersUrl}?search=${encodeURIComponent(searchTerm)}`;
    return fetchJSON(url);
}

/**
 * Create a new team member with authentication credentials.
 * 
 * @param {Object} data - Team member data
 * @param {string} data.username - Login username (required)
 * @param {string} data.email - Email address (required)
 * @param {string} data.password - Initial password (required, min 8 chars)
 * @param {string} [data.status_code='active'] - Account status
 * @param {string} [data.first_name] - First name
 * @param {string} [data.last_name] - Last name
 * @param {string} [data.is_admin='n'] - Admin flag ('y' or 'n')
 * @param {string} [data.is_agent='e'] - Agent flag ('e', 'a', or 'y')
 * @returns {Promise<Object>} { success, message, data: {...} }
 */
export async function createTeamMember(data) {
    return fetchJSON(apiTeamMembersUrl, {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

/**
 * Update an existing team member.
 * 
 * NOTE: This does NOT update passwords. Use resetTeamMemberPassword() instead.
 * 
 * @param {number} teamMemberId - ARCIMEDES_ROW_ID
 * @param {Object} data - Fields to update (partial update allowed)
 * @returns {Promise<Object>} { success, message, data: {...} }
 */
export async function updateTeamMember(teamMemberId, data) {
    return fetchJSON(`${apiTeamMembersUrl}/${teamMemberId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
}

/**
 * Reset a team member's password.
 * 
 * @param {number} teamMemberId - ARCIMEDES_ROW_ID
 * @param {string} password - New password (min 8 characters)
 * @returns {Promise<Object>} { success, message }
 */
export async function resetTeamMemberPassword(teamMemberId, password) {
    return fetchJSON(`${apiTeamMembersUrl}/${teamMemberId}/password`, {
        method: 'PUT',
        body: JSON.stringify({ password }),
    });
}

/**
 * Activate a team member (set STATUS_CODE = 'active').
 * 
 * @param {number} teamMemberId - ARCIMEDES_ROW_ID
 * @returns {Promise<Object>} { success, message, data: {...} }
 */
export async function activateTeamMember(teamMemberId) {
    return fetchJSON(`${apiTeamMembersUrl}/${teamMemberId}/activate`, {
        method: 'PUT',
    });
}

/**
 * Deactivate a team member (set STATUS_CODE = 'inactive').
 * User will not be able to log in after deactivation.
 * 
 * @param {number} teamMemberId - ARCIMEDES_ROW_ID
 * @returns {Promise<Object>} { success, message, data: {...} }
 */
export async function deactivateTeamMember(teamMemberId) {
    return fetchJSON(`${apiTeamMembersUrl}/${teamMemberId}/deactivate`, {
        method: 'PUT',
    });
}

/**
 * Permanently delete a team member (hard delete).
 * 
 * WARNING: This action is irreversible.
 * 
 * @param {number} teamMemberId - ARCIMEDES_ROW_ID
 * @returns {Promise<Object>} { success, message }
 */
export async function deleteTeamMember(teamMemberId) {
    return fetchJSON(`${apiTeamMembersUrl}/${teamMemberId}/purge`, {
        method: 'DELETE',
    });
}

/**
 * List all team members with optional filters.
 * 
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.status] - Filter by status_code ('active', 'inactive')
 * @param {boolean} [filters.is_admin] - Filter admin users only
 * @param {boolean} [filters.is_agent] - Filter agents only
 * @param {string} [filters.search] - Search term
 * @returns {Promise<Object>} { success, data: [...], count }
 */
export async function listTeamMembers(filters = {}) {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status);
    if (filters.is_admin) params.append('is_admin', '1');
    if (filters.is_agent) params.append('is_agent', '1');
    if (filters.search) params.append('search', filters.search);
    
    const url = `${apiTeamMembersUrl}${params.toString() ? '?' + params.toString() : ''}`;
    return fetchJSON(url);
}

/**
 * Read a team member's CRM Hot List setup status.
 *
 * The stored CRM token is never returned by the server — `tokenHint` is only the
 * token's leading ID, enough to tell two tokens apart.
 *
 * @param {string} email - Team member email
 * @returns {Promise<Object>} { configured, rowId?, crmUserId?, crmOpportunityId?, tokenHint?, updatedAt? }
 */
export async function getTeamMemberCrmCredentials(email) {
    return fetchJSON(`${apiCrmCredentialsUrl}?email=${encodeURIComponent(email)}`);
}

/**
 * Verify a team member's personal CRM API token and store their credentials.
 *
 * The server asks the CRM who the token belongs to and which Hot List column is
 * theirs (creating it if needed), then saves the row.
 *
 * @param {string} email - Team member email
 * @param {string} sanctumToken - The team member's personal CRM API token
 * @returns {Promise<Object>} { success, rowId, crmUserId, crmOpportunityId, tokenHint }
 */
export async function saveTeamMemberCrmCredentials(email, sanctumToken) {
    return fetchJSON(`${apiCrmCredentialsUrl}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ teamMemberEmail: email, sanctumToken }),
    });
}
